import { useContext, useEffect, useMemo, useState } from "react"
import { DateTime } from "luxon"
import { playersContext } from "../providers/players.provider"
import { schoolContext } from "../providers/school.provider"
import { getLessonsByDateRange } from "../utils/firestore.utils"
import { getSeasonBounds } from "../utils/substitutes.utils"
import {
	NEARBY_DAYS,
	substituteSuggestions,
} from "../utils/substituteSuggestions.utils"
import useSeasonRecoveryLessons from "./useSeasonRecoveryLessons"

// Chi proporre al posto di `absent` (vedi substituteSuggestions.utils), con
// due letture sole per tutto il circolo: le lezioni della stagione che
// contano per i recuperi, e quelle della settimana prima e dopo, da cui si
// sa chi è già in campo a quell'ora e in che gruppo CAD gioca ognuno. Niente
// lettura per allievo.
//
// `null` finché le due letture non sono arrivate: proporre prima vorrebbe
// dire proporre anche chi a quell'ora è già in un'altra lezione.
const useSubstituteSuggestions = (lessonData, absent, excludedIDs) => {
	const { activePlayers } = useContext(playersContext)
	const { selectedSchool } = useContext(schoolContext)
	const schoolID = lessonData.schoolID ?? selectedSchool

	const lessonStart = lessonData.lessonStart.toDate()
	const time = lessonStart.getTime()
	const seasonStart = useMemo(() => getSeasonBounds(new Date(time)).start, [time])
	const recoveryLessons = useSeasonRecoveryLessons(schoolID, seasonStart)

	const [nearbyLessons, setNearbyLessons] = useState(null)
	useEffect(() => {
		if (!schoolID) return
		let isCurrent = true
		const day = DateTime.fromMillis(time)
		getLessonsByDateRange(
			schoolID,
			null,
			day.minus({ days: NEARBY_DAYS }).toJSDate(),
			day.plus({ days: NEARBY_DAYS }).toJSDate(),
		)
			.then(lessons => isCurrent && setNearbyLessons(lessons))
			.catch(error => {
				// senza, le proposte non si fanno: resta il campo per scrivere
				console.error("Lezioni vicine non lette:", error)
			})
		return () => {
			isCurrent = false
		}
	}, [schoolID, time])

	const excludedKey = [...(excludedIDs || [])].sort().join(",")

	return useMemo(() => {
		if (!recoveryLessons || !nearbyLessons) return null
		return substituteSuggestions({
			players: activePlayers,
			lesson: lessonData,
			absent,
			excludedIDs,
			recoveryLessons,
			nearbyLessons,
		})
		// i partecipanti esclusi contano per chi sono, non per il Set nuovo
		// che arriva a ogni giro
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activePlayers, lessonData, absent, excludedKey, recoveryLessons, nearbyLessons])
}

export default useSubstituteSuggestions
